import React, { useContext, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { EntitiesContext } from '../../../App';
import { useGetSaleOrders } from '../../../hooks/useSale';
import { useGetOrderItems } from '../../../hooks/useOrder';
import * as ActionTypes from '../../../constants/action-types';
import Loader from '../../Loader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const options = {
  responsive: true,
  plugins: {
    legend: {
      position: 'top',
    },
    title: {
      display: true,
      text: 'Sale Totals',
    },
  },
};

export default function SaleStats() {
  const entities = useContext(EntitiesContext);
  const sale = entities.state.get('sale').find(s => s.open);
  const orders = entities.state.get('order');
  const inventory = entities.state.get('inventory');

  const [averageOrderAmount, setAverageOrderAmount] = useState(0);
  const [averageOrderItems, setAverageOrderItems] = useState(1);
  const [data, setData] = useState();

  useGetSaleOrders(sale.id);

  const getItems = useGetOrderItems();

  // always start fresh
  useEffect(() => {
    entities.dispatch({
      type: ActionTypes.CLEAR_ENTITIES,
      payload: 'inventory'
    });
  }, []);

  useEffect(() => {
    if (orders.count() >= 1) {
      Promise.all(orders.filter(o => o.status === 'paid').map(o => getItems.mutateAsync(o)));
    }
  }, [orders]);

  useEffect(() => {
    if (orders.count() >= 1 && inventory.count() >= 1) {
      setAverageOrderAmount(inventory
        .filter(i => i.order_id)
        .reduce((acc, cur) => acc + cur.price, 0) / inventory.count());
      setAverageOrderItems(orders
        .filter(o => o.status === 'paid')
        .map(o => inventory.filter(i => i.order_id === o.id).count())
        .reduce((acc, cur) => acc + cur, 0) / orders.count());

      const labels = sale.ordersByHour(orders).toJS();

      const mapped = labels.map(l => {
        const hourlyOrders = orders.filter(o => o.status === 'paid' && DateTime.fromISO(o.createdAt) <= DateTime.fromSQL(l).plus({ hours: 1 }));
        const total = hourlyOrders.map(o => inventory
          .filter(i => i.order_id === o.id)
          .reduce((acc, cur) => acc + cur.price, 0))
          .reduce((acc, cur) => acc + cur, 0);

        return total;
      });

      setData({
        labels: labels.map(l => DateTime.fromSQL(l).toFormat('t')),
        datasets: [{
          label: 'Totals',
          data: mapped,
          backgroundColor: 'rgba(255, 99, 132, 0.5)'
        }],
      });
    }
  }, [orders, inventory]);

  if (!sale) return <Loader />

  return (
    <div>
      {sale.sale_start}
      <div className="title">Sale Stats for {sale.sale_start}</div>
      {inventory.count() < 1 && 'No current sale data available.'}

      {inventory.count() > 0 && <>
        <nav className="level">
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Average Order Value</p>
              <p className="title">
                ${Math.round(averageOrderAmount)}
              </p>
            </div>
          </div>
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Average Items/Order</p>
              <p className="title">
                {Math.round(averageOrderItems)}
              </p>
            </div>
          </div>
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Total Sales</p>
              <p className="title">
                ${inventory.filter(i => i.order_id).reduce((acc, curr) => acc + curr.price, 0)}
              </p>
            </div>
          </div>
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">Total Processing Fee</p>
              <p className="title">
                ${orders
                    .filter(o => o.status === 'paid' && o.payment_method === 'card')
                    .valueSeq()
                    .reduce((acc, curr) => acc + parseFloat(curr.processing_fee), 0).toFixed(2)}
              </p>
            </div>
          </div>
          <div className="level-item has-text-centered">
            <div>
              <p className="heading">PTA Take</p>
              <p className="title">
                ${inventory.filter(i => i.order_id).reduce((acc, curr) => acc + curr.price, 0) * 0.20}
              </p>
            </div>
          </div>
        </nav>

        {data && <Line
          options={options}
          data={data}
        />}
      </>}
    </div>
  )
}
